import { useState } from "react";
import { Download, Eye, FileText, Image as ImageIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import DeleteWithReasonDialog from "@/components/DeleteWithReasonDialog";
import { FilePreviewDialog } from "./FilePreviewDialog";
import { FileEditDialog } from "./FileEditDialog";
import { categoryLabel, downloadFile, fmtDate, fmtSize } from "./fileUtils";

function IconBtn({ label, onClick, testid, danger, children }) {
  return (
    <button type="button" title={label} aria-label={label} data-testid={testid}
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className={`h-7 w-7 inline-flex items-center justify-center rounded-md text-zinc-500 transition-colors
              ${danger ? "hover:bg-red-50 hover:text-red-700" : "hover:bg-zinc-100 hover:text-zinc-900"}`}>
      {children}
    </button>
  );
}

function FileRow({ f, canDelete, onView, onEdit, onDelete }) {
  const Icon = (f.content_type || "").startsWith("image/") ? ImageIcon : FileText;
  const date = f.document_date ? fmtDate(f.document_date) : `Uploaded ${fmtDate(f.created_at)}`;
  const download = () => downloadFile(f).catch((e) => toast.error("Download failed", { description: e.message }));
  return (
    <li onClick={onView} data-testid={`file-row-${f.id}`}
        className="group flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 transition-colors">
      <Icon className="h-4 w-4 text-zinc-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 min-w-0 text-sm">
          <span className="font-medium text-zinc-900 truncate" title={f.filename} data-testid={`file-name-${f.id}`}>
            {f.filename || "(untitled)"}
          </span>
          <span className="shrink-0 rounded border border-zinc-200 bg-zinc-50 px-1.5 text-2xs uppercase tracking-wide text-zinc-600"
                data-testid={`file-category-${f.id}`}>{categoryLabel(f.category)}</span>
          <span className="shrink-0 text-xs text-zinc-500 font-mono-num" data-testid={`file-date-${f.id}`}>{date}</span>
          {f.size ? <span className="shrink-0 text-2xs text-zinc-400 font-mono-num">{fmtSize(f.size)}</span> : null}
        </div>
        <div className="text-xs text-zinc-500 truncate" data-testid={`file-description-${f.id}`}>
          {f.description || <span className="italic text-zinc-400">No description</span>}
          {f.uploaded_by_name ? ` · by ${f.uploaded_by_name}` : ""}
          {f.edited_at ? ` · edited ${fmtDate(f.edited_at)}` : ""}
        </div>
      </div>
      <div className="flex items-center gap-0.5 shrink-0">
        <IconBtn label="View" onClick={onView} testid={`file-view-${f.id}`}><Eye className="h-4 w-4" /></IconBtn>
        <IconBtn label="Download" onClick={download} testid={`file-download-${f.id}`}><Download className="h-4 w-4" /></IconBtn>
        <IconBtn label="Edit details" onClick={onEdit} testid={`file-edit-${f.id}`}><Pencil className="h-4 w-4" /></IconBtn>
        {canDelete && (
          <IconBtn label="Delete" danger onClick={onDelete} testid={`file-delete-${f.id}`}><Trash2 className="h-4 w-4" /></IconBtn>
        )}
      </div>
    </li>
  );
}

export function ClientFilesList({ files, onChanged }) {
  const { can } = useAuth();
  const canDelete = can("owner", "manager");
  const [viewing, setViewing] = useState(null);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null);

  async function confirmDelete(reason) {
    await api.delete(`/health-record/attachments/${deleting.id}`, { data: { reason } });
    toast.success("File deleted", { description: "An admin can restore it from Deleted Items." });
    onChanged?.();
  }

  return (
    <>
      <ul className="rounded-lg border border-zinc-200 divide-y divide-zinc-100 overflow-hidden" data-testid="files-list">
        {files.map((f) => (
          <FileRow key={f.id} f={f} canDelete={canDelete} onView={() => setViewing(f)}
                   onEdit={() => setEditing(f)} onDelete={() => setDeleting(f)} />
        ))}
      </ul>
      {viewing && <FilePreviewDialog file={viewing} onClose={() => setViewing(null)} />}
      {editing && (
        <FileEditDialog file={editing} onClose={() => setEditing(null)}
                        onSaved={() => { setEditing(null); onChanged?.(); }} />
      )}
      <DeleteWithReasonDialog
        open={!!deleting}
        onOpenChange={(v) => !v && setDeleting(null)}
        title="Delete this document?"
        description="The document will be hidden from this client's record. An admin can restore it later from Deleted Items."
        itemLabel={deleting?.filename || "Attachment"}
        confirmLabel="Delete document"
        onConfirm={confirmDelete}
        testidPrefix="file-delete-dialog"
      />
    </>
  );
}
